import { Alert, EmptyState, VStack } from "@chakra-ui/react"
import { FiEye } from "react-icons/fi"
import { QuizInterface } from "../TestTaking/QuizInterface"

import type { QuizSection, SelectedQuestion } from "./quiz-builder-types"

interface QuizPreviewProps {
  quizName: string
  sections: QuizSection[]
  unsectionedQuestions: SelectedQuestion[]
  showRationaleMode?: string
  hasUnsavedChanges?: boolean
}

export function QuizPreview({
  quizName,
  sections,
  unsectionedQuestions,
  showRationaleMode = "after_all",
  hasUnsavedChanges = false,
}: QuizPreviewProps) {
  const previewQuestions = [
    ...sections.flatMap((section) =>
      section.questions.map((item) => ({
        id: item.id,
        question_text: item.data.question_text,
        options: Array.isArray(item.data.options) ? item.data.options : [],
        rationale: item.data.rationale,
        section: {
          id: section.id,
          name: section.name,
        },
      })),
    ),
    ...unsectionedQuestions.map((item) => ({
      id: item.id,
      question_text: item.data.question_text,
      options: Array.isArray(item.data.options) ? item.data.options : [],
      rationale: item.data.rationale,
      section: null,
    })),
  ]

  const totalQuestions = previewQuestions.length

  const hasQuestions = totalQuestions > 0
  const previewId = previewQuestions.map((question) => question.id).join(":")

  return (
    <VStack align="stretch" gap={4}>
      <Alert.Root status={hasUnsavedChanges ? "warning" : "info"}>
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Preview uses saved changes</Alert.Title>
          <Alert.Description>
            Preview is based on the latest saved quiz changes.
            {hasUnsavedChanges
              ? " You have unsaved changes. Save the quiz to update this preview."
              : ""}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>

      {!hasQuestions ? (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FiEye />
            </EmptyState.Indicator>
            <VStack textAlign="center">
              <EmptyState.Title>No questions selected</EmptyState.Title>
              <EmptyState.Description>
                Add questions to see the quiz preview here
              </EmptyState.Description>
            </VStack>
          </EmptyState.Content>
        </EmptyState.Root>
      ) : (
        <QuizInterface
          quiz={{
            id: `quiz-builder-preview-${previewId}`,
            name: quizName || "Untitled Quiz",
            questions: previewQuestions,
          }}
          attemptId={`quiz-builder-preview-${previewId}`}
          onAnswerSelect={() => undefined}
          onSubmit={() => undefined}
          showRationaleMode={showRationaleMode}
        />
      )}
    </VStack>
  )
}
